import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SplashScreen as NativeSplash } from '@capacitor/splash-screen';
import { isNative } from '@/lib/api-base';
import { useTranslation } from 'react-i18next';

interface SplashScreenProps {
  audioSrc?: string;
  logoSrc?: string;
  fallbackMs?: number;
  onComplete: () => void;
}

/**
 * Cinematic boot splash for the Capacitor app.
 *
 * Choreography (audio-synced over ~3s):
 *   ── Entrance ──
 *   t=0       hide native splash → React canvas (#0A0B0F) takes over
 *   t=0.1s    halo blooms outward from center (cobalt → teal radial)
 *   t=0.2s    logo: opacity 0→1, scale 0.6→1.0, rotate -8°→0° (drop-in)
 *   t=0.7s    wordmark "my360" slides in from logo center, blur-clears
 *   t=2.0s    tagline fades in below
 *   ── Exit ──
 *   t=2.7s    everything starts fading + scaling 1.0→1.04 (lift-off feel)
 *   t=3.0s    onComplete fires (audio ends or fallback timer)
 *
 * Why this looks cinematic vs the previous version:
 *   - Logo HAS an entrance — it doesn't just "be there" matching the
 *     native splash. The native splash hides BEFORE the React splash
 *     paints (controlled by SplashScreen.hide() w/ 200ms fade), giving
 *     a clean dark frame for the React entrance to land on. No double
 *     logo flash because there's a brief blackout between the two.
 *   - Wordmark uses var(--font-logo) (Leckerli One) — brand voice.
 *   - Halo+logo+wordmark all use coordinated easing curves, not
 *     bouncy / overshoot — keeps it premium not playful.
 */
export function SplashScreen({
  audioSrc = '/splash-startup.mp3',
  logoSrc = '/logo.png',
  fallbackMs = 3000,
  onComplete,
}: SplashScreenProps) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fallbackRef = useRef<number | null>(null);
  const completedRef = useRef(false);
  const [exiting, setExiting] = useState(false);

  // Hide the native Capacitor splash before our entrance runs. The
  // 200ms fadeOut leaves a brief blackout (~120ms) so React's entrance
  // doesn't overlap with the native logo's tail — avoiding the double
  // logo flash from earlier iterations.
  useEffect(() => {
    if (!isNative()) return;
    NativeSplash.hide({ fadeOutDuration: 200 }).catch(() => {
      /* plugin unavailable — native splash auto-hides on first paint */
    });
  }, []);

  useEffect(() => {
    const audio = new Audio(audioSrc);
    audioRef.current = audio;
    audio.preload = 'auto';

    const finish = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      setExiting(true);
      window.setTimeout(() => onComplete(), 420); // wait for exit anim
    };

    audio.addEventListener('ended', finish);
    audio.play().catch(() => {
      /* autoplay blocked — fallback timer drives the close */
    });
    fallbackRef.current = window.setTimeout(finish, fallbackMs);

    return () => {
      audio.removeEventListener('ended', finish);
      try {
        audio.pause();
      } catch {
        /* noop */
      }
      audioRef.current = null;
      if (fallbackRef.current) {
        clearTimeout(fallbackRef.current);
        fallbackRef.current = null;
      }
    };
  }, [audioSrc, fallbackMs, onComplete]);

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0A0B0F] overflow-hidden"
          aria-label="my360"
          role="presentation"
        >
          {/* Halo — blooms behind the logo over the full splash duration */}
          <motion.div
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{
              opacity: [0, 0.6, 0.7, 0.5, 0],
              scale: [0.4, 1.0, 1.3, 1.6, 1.9],
            }}
            transition={{ duration: 3, ease: [0.16, 1, 0.3, 1], times: [0, 0.2, 0.5, 0.8, 1] }}
            className="absolute h-[60vmin] w-[60vmin] rounded-full pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(0,102,255,0.6) 0%, rgba(45,212,191,0.2) 50%, transparent 75%)',
              filter: 'blur(50px)',
            }}
          />

          {/* Brand lockup — icon + wordmark animate as ONE unit.
              Container handles the global enter/exit (scale + opacity);
              children handle their own staggered reveals inside. */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{
              opacity: [0, 1, 1, 1, 0],
              scale: [0.85, 1, 1, 1.02, 1.06],
            }}
            transition={{ duration: 3, ease: [0.32, 0.72, 0, 1], times: [0, 0.2, 0.85, 0.95, 1] }}
            className="relative flex items-center gap-4 select-none pointer-events-none"
          >
            {/* Logo — drops in with a tiny rotation for cinematic feel */}
            <motion.img
              src={logoSrc}
              alt=""
              draggable={false}
              initial={{ opacity: 0, scale: 0.6, rotate: -8, y: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0, y: 0 }}
              transition={{ duration: 0.85, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="w-[22vmin] h-[22vmin] max-w-[140px] max-h-[140px] object-contain"
              style={{ filter: 'drop-shadow(0 12px 32px rgba(0,102,255,0.5))' }}
            />
            {/* Wordmark — slides from inside-out, blur-clears */}
            <motion.span
              initial={{ opacity: 0, x: -28, filter: 'blur(8px)' }}
              animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.7, delay: 0.65, ease: [0.32, 0.72, 0, 1] }}
              className="leading-none text-white"
              style={{
                fontFamily: 'var(--font-logo)',
                fontSize: 'clamp(40px, 11vmin, 72px)',
                letterSpacing: '-0.01em',
              }}
            >
              {/* eslint-disable i18next/no-literal-string */}
              <span style={{ color: '#fff' }}>my</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#0066FF] to-[#2DD4BF]">360</span>
              {/* eslint-enable i18next/no-literal-string */}
            </motion.span>
          </motion.div>

          {/* Tagline — appears late, fades with the rest at exit */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: [0, 1, 1, 0], y: [6, 0, 0, 0] }}
            transition={{ duration: 3, ease: 'easeOut', times: [0, 0.7, 0.85, 1] }}
            className="absolute bottom-[max(env(safe-area-inset-bottom),32px)] left-0 right-0 text-center"
          >
            <div className="text-[10px] uppercase tracking-[0.32em] text-white/40 font-medium">
              {t('componentsMisc:splashScreen.tagline')}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
